import { useCallback, useEffect, useState } from 'react';
import { TriggerType, TriggerTypeForm } from '../../../type';
import { createTriggerType, getTriggerTypes, updateTriggerType } from 'services/asset';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { useFlash } from 'contexts/flash';
import { useFetchTriggerTypes } from 'store/triggerType';

// Self-contained: fetches its own full list (small master-data table, not a
// paginated dataset) rather than routing through ValidationsPage's central
// page/sort/search hook, which only the 4 large tabs use.
export const useInjection = () => {
  const { setFlash } = useFlash();
  const refreshTriggerTypeStore = useFetchTriggerTypes();

  const [triggerTypes, setTriggerTypes] = useState<TriggerType[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModalAddNew, setShowModalAddNew] = useState(false);
  const [typeEdit, setTypeEdit] = useState<TriggerType | undefined>(undefined);

  const fetchTriggerTypes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getTriggerTypes();
      setTriggerTypes(data.maintenanceTriggerTypes || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTriggerTypes();
  }, [fetchTriggerTypes]);

  const handleAddType = useCallback(
    async (value: TriggerTypeForm) => {
      await createTriggerType({ name: value.name });
      setFlash({ type: 'success', message: 'Create trigger type successfully' });
      setShowModalAddNew(false);
      await Promise.all([fetchTriggerTypes(), refreshTriggerTypeStore()]);
    },
    [fetchTriggerTypes, refreshTriggerTypeStore, setFlash]
  );
  const { asyncCallback: asyncAddNewType } = useAsyncCallback(handleAddType, []);

  const handleEditType = useCallback(
    async (value: TriggerTypeForm) => {
      if (!typeEdit) return;
      await updateTriggerType(typeEdit.id, { name: value.name });
      setFlash({ type: 'success', message: 'Update trigger type successfully' });
      setTypeEdit(undefined);
      await Promise.all([fetchTriggerTypes(), refreshTriggerTypeStore()]);
    },
    [typeEdit, fetchTriggerTypes, refreshTriggerTypeStore, setFlash]
  );
  const { asyncCallback: asyncEditType } = useAsyncCallback(handleEditType, []);

  return {
    triggerTypes,
    loading,
    showModalAddNew,
    setShowModalAddNew,
    typeEdit,
    setTypeEdit,
    asyncAddNewType,
    asyncEditType
  };
};
